import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GlobalContext } from './context/GlobalContext';
import { getApir, getToken } from './lib/haxios';
import logo from './images/logo.png';
import brandMark from './images/256x256.png';
import bgImage from './images/bgSA.png';

// Remember-me still stores plaintext credentials in localStorage to match
// current behaviour — replacement with refresh tokens is tracked in
// BACKEND_CHANGES.md (item B-001).

const Login = () => {
  const nav = useNavigate();
  const { loginError, setLoginError, setLoading, setCinfo, setApir, setUinfo } = useContext(GlobalContext);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd,  setShowPwd]  = useState(false);
  const [remember, setRemember] = useState(false);
  const [busy,     setBusy]     = useState(false);
  const [clock,    setClock]    = useState(() => new Date());

  useEffect(() => {
    setLoginError('');
    const remembered = localStorage.getItem('rememberMe') === 'true';
    const u = localStorage.getItem('rememberedUsername');
    const p = localStorage.getItem('rememberedPassword');
    if (remembered && u && p) {
      setUsername(u);
      setPassword(p);
      setRemember(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const id = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  const submit = async (e) => {
    e?.preventDefault();
    if (!username || !password) {
      setLoginError('Please enter both username and password');
      return;
    }
    setBusy(true);
    setLoginError('');
    try {
      const res = await getToken(username, password);
      if (!res) { setLoginError('Invalid username or password.'); return; }
      if (remember) {
        localStorage.setItem('rememberMe', 'true');
        localStorage.setItem('rememberedUsername', username);
        localStorage.setItem('rememberedPassword', password);
      } else {
        localStorage.removeItem('rememberMe');
        localStorage.removeItem('rememberedUsername');
        localStorage.removeItem('rememberedPassword');
      }
      // The login response carries the full identity (company + operator), so
      // there is no separate hydration round-trip.
      const user = res.user;
      const apirUrl = await getApir();
      setCinfo(user);
      setApir(apirUrl);
      setUinfo(user);
      // Persist Inits so haxios can stamp the actor on BDM writes after a refresh.
      if (user?.Inits) localStorage.setItem('userInits', user.Inits);
      setLoading(false);
      // First login / admin-issued temp password → force a password change.
      nav(res.must_reset ? '/change-password' : '/');
    } catch (err) {
      console.error('Login error:', err);
      setLoginError('Login failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const hh = String(clock.getHours()).padStart(2,'0');
  const mm = String(clock.getMinutes()).padStart(2,'0');
  const ss = String(clock.getSeconds()).padStart(2,'0');
  const dateLine = clock.toLocaleDateString('en-GB', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  }).toUpperCase();

  return (
    <div className="login-screen">
      <div className="login-bg" aria-hidden="true">
        <div className="login-photo" style={{ backgroundImage: `url(${bgImage})` }}/>
        <div className="login-photo-tint"/>
        <div className="login-grid"/>
        <div className="login-horizon"/>
        <div className="login-scan"/>
        <div className="login-noise"/>
      </div>

      <div className="login-stage">
        <header className="login-header">
          <div className="login-brand">
            <span className="login-mark">
              <img src={brandMark} alt="" className="login-mark-img"/>
            </span>
            <div className="login-wordmark">
              <span className="login-name">SALES<span className="accent">ASSIST</span></span>
            </div>
          </div>
          <img src={logo} alt="" className="login-header-logo"/>
        </header>

        <div className="login-card">
          <span className="corner tl" aria-hidden="true"/>
          <span className="corner tr" aria-hidden="true"/>
          <span className="corner bl" aria-hidden="true"/>
          <span className="corner br" aria-hidden="true"/>

          <div className="login-card-strip num">
            <span className="strip-dash"/>
            <span>{hh}:{mm}:{ss}</span>
            <span className="strip-dash"/>
          </div>

          <div className="login-headings">
            <h1>Sign in.</h1>
            <p>Restricted to authorised operators of Sales Assist.</p>
          </div>

          <form className="login-form" onSubmit={submit} noValidate>
            {loginError && (
              <div className="login-error num">{loginError}</div>
            )}

            <label className="login-field">
              <span className="login-label num">Username</span>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                disabled={busy}
                autoFocus
                autoComplete="username"
                placeholder="operator.id"
              />
            </label>

            <label className="login-field">
              <span className="login-label num">
                Password
                <button
                  type="button"
                  className="login-toggle num"
                  onClick={() => setShowPwd(v => !v)}
                  tabIndex={-1}
                >
                  {showPwd ? 'hide' : 'show'}
                </button>
              </span>
              <input
                type={showPwd ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                disabled={busy}
                autoComplete="current-password"
                placeholder="••••••••"
              />
            </label>

            <label className="login-remember">
              <input
                type="checkbox"
                checked={remember}
                onChange={() => setRemember(v => !v)}
              />
              <span className="login-checkbox">{remember && <span>✓</span>}</span>
              <span>Keep me signed in on this device</span>
            </label>

            <button
              className="login-submit"
              type="submit"
              disabled={busy}
            >
              <span>{busy ? 'Signing in…' : 'Sign in'}</span>
              <span className="login-arrow" aria-hidden="true">→</span>
            </button>
          </form>

          <div className="login-card-foot num">
            <span>BUILD 2.6.0</span>
            <span className="strip-dot">·</span>
            <span>{dateLine}</span>
          </div>
        </div>

        <footer className="login-footer num">
          <span>SECURED · TLS 1.3</span>
          <span className="strip-dot">·</span>
          <span>ISO 27001</span>
          <span className="strip-dot">·</span>
          <span>© 2026 AUTOMOTIVE LTD</span>
        </footer>
      </div>
    </div>
  );
};

export default Login;
